import {
  Badge,
  Button,
  Card,
  confirm,
  Modal,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  toast,
} from "@devhop/ui";
import {
  Building2,
  Edit,
  FileText,
  Mail,
  Plus,
  Trash2,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { useJDByPositionId } from "@/modules/jd/presentation/api/queries";
import { JDDetailView } from "@/modules/jd/presentation/ui/JDDetailView";
import {
  useCreatePosition,
  useDeactivatePosition,
  useUpdatePosition,
} from "@/modules/positions/presentation/api/queries";
import type { PositionFormValues } from "@/modules/positions/presentation/ui/PositionForm";
import { PositionForm } from "@/modules/positions/presentation/ui/PositionForm";
import { AppImage } from "@/shared/ui/AppImage";
import { QueryState } from "@/shared/ui/QueryState";
import type { PositionWithDetails } from "../../domain/types";
import { useOrgChartOverview } from "../api/queries";

interface OrgChartSidebarProps {
  selectedPosition: PositionWithDetails | null;
  onClose: () => void;
}

export function OrgChartSidebar({
  selectedPosition,
  onClose,
}: OrgChartSidebarProps) {
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "update">("create");
  const [showJDDialog, setShowJDDialog] = useState(false);

  const { has } = usePermissions();
  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition(selectedPosition?.id || "");
  const deactivatePosition = useDeactivatePosition();
  const { refetch } = useOrgChartOverview({});
  const {
    data: jdData,
    isLoading: isLoadingJD,
    error: jdError,
  } = useJDByPositionId(showJDDialog ? selectedPosition?.id : undefined);

  const handleCreatePosition = () => {
    setFormMode("create");
    setShowPositionForm(true);
  };

  const handleUpdatePosition = () => {
    setFormMode("update");
    setShowPositionForm(true);
  };

  const handleDeletePosition = async () => {
    if (!selectedPosition) return;

    const confirmed = await confirm({
      title: "ຢືນຢັນການລຶບຕໍາແໜ່ງ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຕໍາແໜ່ງ "${selectedPosition.title}"?`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });

    if (confirmed) {
      toast.promise(deactivatePosition.run(selectedPosition.id), {
        loading: "ກຳລັງລຶບຕໍາແໜ່ງ...",
        success: "ລຶບຕໍາແໜ່ງສຳເລັດ",
        error: "ລຶບຕໍາແໜ່ງລ່ຽມລົ້ມເຫຼວ",
      });
      onClose();
      await refetch();
    }
  };

  const handlePositionFormSubmit = async (values: PositionFormValues) => {
    try {
      if (formMode === "create") {
        await createPosition.mutateAsync(values);
      } else if (formMode === "update" && selectedPosition) {
        await updatePosition.mutateAsync(values);
      }
      setShowPositionForm(false);
      // Refresh org chart data
      await refetch();
    } catch (error) {
      console.error("Error saving position:", error);
    }
  };

  return (
    <Sheet
      open={!!selectedPosition}
      onOpenChange={(open) => !open && onClose()}
      data-org-chart-sidebar
    >
      <SheetContent
        side="right"
        className="w-80 p-4 sm:w-96"
        data-org-chart-sidebar
      >
        <SheetHeader>
          <SheetTitle>ລາຍລະອຽດຕໍາແໜ່ງ</SheetTitle>
        </SheetHeader>

        <div className="mt-6">
          {selectedPosition && (
            <div className="space-y-4">
              {/* Position Info */}
              <Card className="p-4">
                <h3 className="mb-2 font-semibold text-lg">
                  {selectedPosition.title}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedPosition.department?.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline">
                      Level {selectedPosition.level}
                    </Badge>
                  </div>
                </div>
              </Card>

              {/* Employee Info */}
              {selectedPosition.employees &&
              selectedPosition.employees.length > 0 ? (
                <Card className="p-4">
                  <h4 className="mb-3 flex items-center space-x-2 font-medium">
                    <Users className="h-4 w-4" />
                    <span>ພະນັກງານ ({selectedPosition.employees.length})</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedPosition.employees.map((employee) => (
                      <div
                        key={employee.id}
                        className="flex items-start space-x-3 rounded-lg border p-2"
                      >
                        <div className="size-10 shrink-0 overflow-hidden rounded-full bg-muted">
                          {employee.image ? (
                            <AppImage
                              src={employee.image}
                              alt={employee.name}
                              className="size-full"
                              fit="cover"
                              showLoading={false}
                              fallback={
                                <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                                  <span className="font-medium text-muted-foreground text-xs">
                                    {employee.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              }
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                              <span className="font-medium text-muted-foreground text-xs">
                                {employee.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="font-medium text-sm">{employee.name}</p>
                          <div className="flex items-center space-x-2 text-muted-foreground text-xs">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{employee.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="border-dashed p-4">
                  <div className="text-center text-muted-foreground">
                    <User className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm">ຕໍາແໜ່ງນີ້ຍັງວ່າງ</p>
                  </div>
                </Card>
              )}

              {/* Reports */}
              {selectedPosition.reportsCount &&
                selectedPosition.reportsCount > 0 && (
                  <Card className="p-4">
                    <h4 className="mb-2 flex items-center space-x-2 font-medium">
                      <Users className="h-4 w-4" />
                      <span>ຕໍາແໜ່ງລ່ຽມ</span>
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      ມີຕໍາແໜ່ງລ່ຽມ {selectedPosition.reportsCount} ຕໍາແໜ່ງ
                    </p>
                  </Card>
                )}

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  size="sm"
                  onClick={() => setShowJDDialog(true)}
                  disabled={!selectedPosition.hasJD}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  ລາຍລະອຽດວຽກ
                </Button>

                {has("positions:create") && (
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={handleCreatePosition}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    ເພີ່ມຕໍາແໜ່ງລ່ຽມ
                  </Button>
                )}

                {has("positions:update") && (
                  <Button
                    variant="outline"
                    className="w-full"
                    size="sm"
                    onClick={handleUpdatePosition}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    ແກ້ໄຂ
                  </Button>
                )}

                {has("positions:update") &&
                  (!selectedPosition.employees ||
                    selectedPosition.employees.length === 0) && (
                    <Button
                      variant="destructive"
                      className="w-full"
                      size="sm"
                      onClick={handleDeletePosition}
                      disabled={deactivatePosition.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      ລຶບ
                    </Button>
                  )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      {/* Position Form Modal */}
      <Modal
        open={showPositionForm}
        onOpenChange={setShowPositionForm}
        title={formMode === "create" ? "ເພີ່ມຕໍາແໜ່ງໃໝ່" : "ແກ້ໄຂຕໍາແໜ່ງ"}
      >
        <PositionForm
          initialValues={
            formMode === "update" && selectedPosition
              ? {
                  title: selectedPosition.title,
                  departmentId:
                    selectedPosition.departmentId ||
                    selectedPosition.department?.id ||
                    "",
                  parentId: null, // TODO: Need to fetch full position data to get parentId
                  maxEmployees: null, // TODO: Need to fetch full position data to get maxEmployees
                }
              : {
                  parentId: selectedPosition?.id || null, // Set current position as parent for create
                }
          }
          onSubmit={handlePositionFormSubmit}
          submitting={
            createPosition.isPending ||
            updatePosition.isPending ||
            deactivatePosition.isPending
          }
          excludePositionId={
            formMode === "update" ? selectedPosition?.id : undefined
          }
        />
      </Modal>

      {/* JD Detail Dialog */}
      <Modal
        open={showJDDialog}
        onOpenChange={setShowJDDialog}
        title="ລາຍລະອຽດ JD"
        className="flex max-h-[90vh] max-w-6xl! flex-col"
      >
        <div className="flex-1 overflow-y-auto pr-2">
          <QueryState
            isLoading={isLoadingJD}
            isError={!!jdError}
            error={jdError}
          >
            {jdData ? (
              <JDDetailView jd={jdData} hideVersionHistory hideHeader />
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <FileText className="mx-auto mb-2 h-12 w-12 text-muted-foreground/50" />
                <p>ບໍ່ພົບ JD ທີ່ຜູກກັບຕໍາແໜ່ງນີ້</p>
              </div>
            )}
          </QueryState>
        </div>
      </Modal>
    </Sheet>
  );
}
